import { useMemo } from "react";
import { useLocation } from "react-router-dom";
import SEO from "./SEO";

const SITE_URL = "https://plotdivision.com";
const DEFAULT_DESCRIPTION = "Plot Division helps writers plan stories, build worlds, draft books, and organize their creative universe.";

const PUBLIC_META = {
  "/": {
    title: "Plot Division | Story Planning and Writing Software",
    description: DEFAULT_DESCRIPTION,
    topic: "story writing software",
    schemaType: "SoftwareApplication",
  },
  "/tools/generators": {
    title: "Free Writing Generators | Plot Division",
    description: "Use free writing generators for book titles, character names, story ideas, place names, and creative prompts.",
    topic: "free writing generators",
    schemaType: "WebApplication",
  },
  "/creator-program": {
    title: "Creator Program | Plot Division",
    description: "Learn about the Plot Division creator program for writers and creative community contributors.",
    topic: "writer creator program",
  },
  "/community-guidelines": {
    title: "Community Guidelines | Plot Division",
    description: "Read Plot Division community guidelines for safe, respectful participation across the writing platform.",
    topic: "writing community guidelines",
  },
  "/copyright": {
    title: "Copyright Policy | Plot Division",
    description: "Review Plot Division copyright policies for writing, stories, prompts, and creative content on the platform.",
    topic: "writing platform copyright policy",
  },
  "/privacy": {
    title: "Privacy Policy | Plot Division",
    description: "Read how Plot Division handles account, writing, privacy, and platform data.",
    topic: "Plot Division privacy policy",
  },
  "/tos": {
    title: "Terms of Service | Plot Division",
    description: "Review the Plot Division terms of service for writers, readers, and creators using the platform.",
    topic: "Plot Division terms of service",
  },
};

const PRIVATE_PREFIXES = [
  "/admin", "/dashboard", "/authorsDesk", "/settings", "/account", "/messages", "/notifications",
  "/billing", "/purchase", "/reset-password", "/verify-email", "/creator-program/setup", "/books",
  "/characters", "/prompt", "/profile", "/quills", "/inks", "/login", "/register", "/forgot-password",
];

function isPrivatePath(pathname) {
  return PRIVATE_PREFIXES.some((prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`));
}

function schemaFor(pathname, meta) {
  const canonical = `${SITE_URL}${pathname === "/" ? "/" : pathname}`;
  const base = [
    {
      "@context": "https://schema.org",
      "@type": "WebSite",
      name: "Plot Division",
      url: SITE_URL,
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: pathname === "/" ? [
        { "@type": "ListItem", position: 1, name: "Home", item: `${SITE_URL}/` },
      ] : [
        { "@type": "ListItem", position: 1, name: "Home", item: `${SITE_URL}/` },
        { "@type": "ListItem", position: 2, name: meta.title.split("|")[0].trim(), item: canonical },
      ],
    },
  ];

  if (meta.schemaType === "SoftwareApplication" || meta.schemaType === "WebApplication") {
    base.push({
      "@context": "https://schema.org",
      "@type": meta.schemaType,
      name: "Plot Division",
      url: canonical,
      applicationCategory: "WritingApplication",
      operatingSystem: "Web",
      description: meta.description,
    });
  }

  return base;
}

export default function RouteSEO() {
  const location = useLocation();
  const pathname = location.pathname.replace(/\/$/, "") || "/";

  const meta = useMemo(() => {
    if (PUBLIC_META[pathname]) {
      return { ...PUBLIC_META[pathname], robots: "index, follow, max-image-preview:large" };
    }

    if (isPrivatePath(pathname)) {
      return {
        title: "Plot Division",
        description: DEFAULT_DESCRIPTION,
        robots: "noindex, nofollow",
        noindex: true,
      };
    }

    return {
      title: "Page Not Found | Plot Division",
      description: "The requested Plot Division page could not be found.",
      robots: "noindex, follow",
      noindex: true,
    };
  }, [pathname]);

  const canonicalPath = PUBLIC_META[pathname] ? pathname : "/";
  const canonical = `${SITE_URL}${canonicalPath === "/" ? "/" : canonicalPath}`;
  const schema = PUBLIC_META[pathname] ? schemaFor(canonicalPath, meta) : null;

  return <SEO {...meta} url={canonical} schema={schema} />;
}